/**
 * useFormXlsxExport Hook
 *
 * Extracts form-level multi-table XLSX export logic.
 * Fetches all tables from the form layout, builds snapshots, and downloads a workbook.
 *
 * Holds NO form subscription. Every value it needs is consumed inside
 * `handleExportFormXlsx`, so the form is read once with `snapshot(formStore.state)`
 * at the top of the handler — before the first await, per the stale-proxy rule.
 * It previously called `useFormStore()`, whose spread of `formVersion.data`
 * re-rendered every consumer on each keystroke; because this hook is composed
 * into `FormWorkflowSelect` and `useArTableExportActions`, that one call
 * re-coarsened two controls that had already been narrowed (SYD-2264).
 *
 * Outside the internal view it runs `resolveExportVisibilityGate`, the predicate
 * the other two XLSX producers share: the active view's `hideKitComponents` /
 * `hideGroupComponents` flags live on `parameters/organization`, so a listener
 * that failed before delivering would let member rows a customer-facing view
 * withholds reach the sheet. On the internal view no flag applies, and only the
 * cosmetic org header fields are lost (SYD-2279).
 */

import { useState, useCallback, useContext } from 'react';
import { snapshot } from 'valtio';
import { formStore } from '../../../store';
import { FieldTypeContext } from '@/providers/FieldTypeContext';
import { MessageContext } from '@/providers/MessageContext';
import { useOrganizationParameters } from '@/use/useOrganizationParameters';
import { resolveExportVisibilityGate } from '@/use/resolveExportVisibilityGate';
import { useVisibilityMode } from '@/use/useVisibilityMode';
import { elementVisibility } from '@/components/rendering/elementVisibility';
import createFormEmailHeaderRows from '../../modals/FormEmailModal/create/createFormEmailHeaderRows';
import discoverArTablesInLayoutStructure from '../../modals/FormEmailModal/create/discoverArTablesInLayoutStructure';
import createArTableExportSnapshot from '../../modals/FormEmailModal/create/createArTableExportSnapshot';
import createXlsxWorkbookBlob from '../../modals/FormEmailModal/create/createXlsxWorkbookBlob';
import fetchXlsxLogoImage from '../../modals/FormEmailModal/create/fetchXlsxLogoImage';
import type { FormVersion, SplitQuery, UIElement } from '#types/System.types';
import type { ArRowId } from '#types/System.Artifacts.types';

type UseFormXlsxExportReturn = {
	handleExportFormXlsx: () => Promise<void>;
	exportingFormXlsx: boolean;
};

function useFormXlsxExport(): UseFormXlsxExportReturn {
	const [exportingFormXlsx, setExportingFormXlsx] = useState(false);

	const { messageApi } = useContext(MessageContext);
	const visibilityMode = useVisibilityMode();
	const { fieldTypes } = useContext(FieldTypeContext);
	const {
		data: orgParams,
		loading: orgParamsLoading,
		error: orgParamsError,
		getVisibilityTypesForFormType,
	} = useOrganizationParameters();

	const handleExportFormXlsx = useCallback(async () => {
		const live = snapshot(formStore.state);

		const gate = resolveExportVisibilityGate({ visibilityMode, orgParams, orgParamsLoading, orgParamsError });
		if (gate) {
			messageApi[gate.severity](gate.message);
			return;
		}

		const layoutStructure = live.formType.data?.defaultUILayout?.structure as UIElement | undefined;
		const fields = live.formVersion.data?.fields as FormVersion['fields'] | undefined;
		const formId = live.formId;
		const versionId = live.versionId;

		if (!layoutStructure) {
			messageApi.error('No form layout found for XLSX export.');
			return;
		}
		if (!fields) {
			messageApi.error('No form fields found for XLSX export.');
			return;
		}
		if (!versionId || !formId) {
			messageApi.error('No form version selected for XLSX export.');
			return;
		}

		setExportingFormXlsx(true);
		try {
			const formNumber = live.form.data?.formNumber;
			const formVersionNumber = live.formVersion.data?.formVersionNumber;
			const formLabel = formNumber && formVersionNumber !== undefined
				? `${formNumber}-v${formVersionNumber}`
				: formNumber;

			const headerRows = createFormEmailHeaderRows({
				formNumber: formLabel,
				orgName: orgParams?.name,
				orgEmail: orgParams?.email,
				orgPhone: orgParams?.phone,
				layoutStructure,
				fields,
				fieldTypes,
				visibilityMode,
			});

			const allTables = discoverArTablesInLayoutStructure(layoutStructure);
			const tables = visibilityMode
				? allTables.filter((t) => elementVisibility(t, visibilityMode))
				: allTables;
			if (tables.length === 0) {
				messageApi.warning('No tables available to export.');
				return;
			}

			const visibilityType = visibilityMode && visibilityMode !== 'internal'
				? getVisibilityTypesForFormType(live.formType.data?.docId).find((v) => v.id === visibilityMode)
				: undefined;

			const parentPath = `forms/${formId}/formVersions/${versionId}`;
			const tableSnapshots = await Promise.all(
				tables.map((table) => {
					const slot = formStore.getTableSlot(parentPath, table.tableId);
					const selectedRowIds = slot
						? (snapshot(slot.ui.selection).selectedRowIds as ArRowId[])
						: null;
					return createArTableExportSnapshot({
						formId,
						versionId,
						table,
						visibilityMode,
						visibilityType,
						fieldTypes,
						splitQuery: live.splitQuery as SplitQuery | undefined,
						selectedRowIds,
						formKitRowColumnCalc: live.formType.data?.kitRowColumnCalc,
					});
				})
			);

			const sanitize = (str: string) => str.replace(/[^a-zA-Z0-9-_]/g, '-');
			const today = new Date().toISOString().split('T')[0];
			const baseParts = [formLabel, 'tables'].filter(Boolean) as string[];
			const filenameBase = baseParts.length > 0 ? sanitize(baseParts.join('-')) : 'export';
			const filename = `${filenameBase}-${today}.xlsx`;

			const logoImage = await fetchXlsxLogoImage(orgParams?.logoUrl);

			const { blob, filename: xlsFilename } = await createXlsxWorkbookBlob({
				headerRows,
				tableSnapshots,
				filename,
				logoImage,
				logoExportSizeMm: orgParams?.logoExportSize,
			});

			const url = window.URL.createObjectURL(blob);
			try {
				const link = document.createElement('a');
				link.href = url;
				link.download = xlsFilename;
				link.click();
			} finally {
				window.URL.revokeObjectURL(url);
			}

			messageApi.success('XLSX exported successfully');
		} catch (error) {
			messageApi.error(`Failed to export XLSX: ${error instanceof Error ? error.message : String(error)}`);
		} finally {
			setExportingFormXlsx(false);
		}
	}, [
		messageApi,
		orgParamsLoading,
		orgParams,
		orgParamsError,
		fieldTypes,
		visibilityMode,
		getVisibilityTypesForFormType,
	]);

	return { handleExportFormXlsx, exportingFormXlsx };
}

export { useFormXlsxExport };
export type { UseFormXlsxExportReturn };
